/**
 * Pledges (GH-410, SPEC.md §13.3).
 *
 * A pledge is a promise to give, not a gift. The single rule everything here exists to enforce is
 * that a promise can never be presented, counted, or reported as money the organization has:
 *
 *   - a pledge has its own state machine, disjoint from the donation one
 *   - `countsAsPledged` is the only predicate that admits a pledge to the public thermometer, and
 *     the thermometer reports it on its own line
 *   - conversion is bounded by the promised amount, so a converted pledge cannot be counted twice
 *
 * There is deliberately no function here that turns a pledge into a received total.
 */
import { money } from "./money.js";
import { StateMachine } from "./state-machine.js";
export const PLEDGE_STATES = [
    "draft",
    "phone_verified",
    "active",
    "partially_converted",
    "fulfilled",
    "cancelled",
    "expired",
];
export const pledgeMachine = new StateMachine({
    name: "Pledge",
    initial: "draft",
    progression: ["draft", "phone_verified", "active", "partially_converted", "fulfilled"],
    terminal: ["cancelled", "expired", "fulfilled"],
    transitions: {
        // A pledge nobody can be reminded about is worth nothing, so `active` is only reachable
        // through a proven contact.
        draft: ["phone_verified", "cancelled"],
        phone_verified: ["active", "cancelled"],
        active: ["partially_converted", "fulfilled", "cancelled", "expired"],
        // Instalments move `converted_minor`, not the state, so re-entering this state is a duplicate
        // delivery rather than progress — `classify` reports it as such and the caller ignores it.
        partially_converted: ["partially_converted", "fulfilled", "cancelled", "expired"],
        fulfilled: [],
        cancelled: [],
        expired: [],
    },
});
/** Outstanding promises only. A fulfilled pledge became a donation and is counted there. */
export function countsAsPledged(state) {
    return state === "active" || state === "partially_converted";
}
/**
 * The amount to record for a pledge taken at checkout.
 *
 * Deliberately the donation component alone, never the quoted total. Optional cost support pays a
 * processing fee, and a promise incurs no processing fee — charging one for a payment that has not
 * happened would be inventing a cost. If and when the pledge converts, that gift is quoted afresh
 * and cost support is offered then.
 */
export function pledgeAmountFrom(quote) {
    return quote.donation;
}
export const PLEDGE_MIN_MINOR = 100n;
/**
 * How the contact behind a pledge was established. Recorded, never assumed.
 *
 * `email` since 2026-09-15 (TestFlight feedback on the pledge screen: "some people want email, it
 * can be verified that way"). Mirrored by the `pledge_contact_provenance` CHECK in migration 0064.
 */
export const PLEDGE_CONTACT_PROVENANCE = ["sms", "email", "wallet", "staff"];
/**
 * Whether a provenance is strong enough to activate a pledge a donor made for themselves.
 *
 * Only a consumed challenge is — `sms` or `email`. A number a wallet handed us is a number the
 * donor gave *Apple or Google*, not one we proved they control right now. It is good enough to
 * skip the typing, never good enough to skip the proof — see `services/api/src/pledge-service.ts`,
 * which accepts exactly two proofs and a wallet's word is neither: a challenge consumed in the
 * pledge transaction, or a verified contact on that channel already on the signed-in donor's own
 * profile, which is itself an earlier challenge (or, for email, a sign-in with Apple or Google,
 * whose issuer proved the mailbox).
 */
export function provenanceProvesControl(provenance) {
    return provenance === "sms" || provenance === "email";
}
/** Confirmation wording. A pledge screen must never be able to read like a receipt. */
export function pledgeConfirmationSummary(amount, organizationDisplayName) {
    const major = `${(amount.amountMinor / 100n).toString(10)}.${(amount.amountMinor % 100n)
        .toString(10)
        .padStart(2, "0")}`;
    return (`You pledged $${major} to ${organizationDisplayName}. ` +
        `Nothing has been charged and no payment details were taken.`);
}
/** Zero of the pledge's currency — used where a caller needs an explicit "nothing converted". */
export function noneConverted(amount) {
    return money(0n, amount.currency);
}
//# sourceMappingURL=pledge.js.map